// src/shared/services/bugReportService.ts
import apiService from '@core/api/apiService';

const BASE = '/v1/user/bug-reports';

export type BugStatus = 'not_fixed' | 'in_fixing' | 'fixed' | 'wont_fix';

/** App-facing shape (camelCase). */
export interface BugReport {
  id: string | number;
  userId: string | number;
  submittedBy: {
    id: string | number;
    name: string;
  };
  isOwner: boolean;
  pageName: string;
  title: string;
  description: string;
  steps?: string;
  url?: string;
  status: BugStatus;
  replies: BugReply[];
  createdAt: string;
}

export interface BugReply {
  id: string | number;
  userId: string | number;
  authorName: string;
  body: string;
  createdAt: string;
  updatedAt?: string;
}

function fromApi(raw: any): BugReport {
  return {
    id: raw.id,
    userId: raw.userId ?? raw.user_id ?? '',
    submittedBy: {
      id: raw.submittedBy?.id ?? raw.submitted_by?.id ?? raw.userId ?? raw.user_id ?? '',
      name: raw.submittedBy?.name ?? raw.submitted_by?.name ?? 'کاربر نامشخص',
    },
    isOwner: Boolean(raw.isOwner ?? raw.is_owner),
    pageName: raw.pageName ?? raw.page_name ?? '',
    title: raw.title ?? '',
    description: raw.description ?? '',
    steps: raw.steps,
    url: raw.url,
    status: raw.status ?? 'not_fixed',
    replies: Array.isArray(raw.replies)
      ? raw.replies.map((reply: any) => ({
          id: reply.id,
          userId: reply.userId ?? reply.user_id ?? '',
          authorName: reply.authorName ?? reply.author_name ?? 'کاربر نامشخص',
          body: reply.body ?? '',
          createdAt: reply.createdAt ?? reply.created_at ?? '',
          updatedAt: reply.updatedAt ?? reply.updated_at ?? undefined,
        }))
      : [],
    createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
  };
}

/** Add a public reply to a bug report. */
export async function replyToBugReport(id: string | number, body: string): Promise<BugReport> {
  const response = await apiService.post<any>(`${BASE}/${id}/replies`, { body });
  const raw = response?.data ?? response;
  return fromApi(raw);
}

/** Edit a reply authored by the current user. */
export async function updateBugReply(reportId: string | number, replyId: string | number, body: string): Promise<BugReport> {
  const response = await apiService.patch<any>(`${BASE}/${reportId}/replies/${replyId}`, { body });
  const raw = response?.data ?? response;
  return fromApi(raw);
}

/** Delete a reply authored by the current user. */
export async function deleteBugReply(reportId: string | number, replyId: string | number): Promise<BugReport> {
  const response = await apiService.delete<any>(`${BASE}/${reportId}/replies/${replyId}`);
  const raw = response?.data ?? response;
  return fromApi(raw);
}

/** Remove the current user's fixed bug reports older than the requested threshold. */
export async function cleanupFixedBugReports(olderThanDays = 30): Promise<number> {
  const response = await apiService.delete<any>(`${BASE}/cleanup-fixed`, {
    data: { older_than_days: olderThanDays },
  });
  return Number(response?.data?.deleted_count ?? response?.deleted_count ?? 0);
}

/** List all bug reports visible to authenticated users. */
export async function getBugReports(): Promise<BugReport[]> {
  const response = await apiService.get<any>(BASE);
  let list: any[] = [];
  if (Array.isArray(response)) list = response;
  else if (response?.data && Array.isArray(response.data)) list = response.data;
  else if (response?.bug_reports && Array.isArray(response.bug_reports)) list = response.bug_reports;
  return list.map(fromApi);
}

/** Create a bug report (associated to current user by backend). */
export async function createBugReport(data: {
  page_name: string;
  title: string;
  description: string;
  steps?: string;
  url?: string;
}): Promise<BugReport> {
  const response = await apiService.post<any>(BASE, data);
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : fromApi({ ...data, id: '', status: 'not_fixed', created_at: new Date().toISOString() });
}

/** Update bug report status. */
export async function updateBugReportStatus(id: string | number, status: BugStatus): Promise<BugReport> {
  const response = await apiService.patch<any>(`${BASE}/${id}`, { status });
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : ({ id, status } as BugReport);
}

/** Update bug report (full fields). */
export async function updateBugReport(
  id: string | number,
  data: { page_name?: string; title?: string; description?: string; steps?: string; url?: string }
): Promise<BugReport> {
  const response = await apiService.patch<any>(`${BASE}/${id}`, data);
  const raw = response?.data ?? response;
  return raw ? fromApi(raw) : ({ id, ...data } as BugReport);
}

/** Delete a bug report. */
export async function deleteBugReport(id: string | number): Promise<void> {
  await apiService.delete(`${BASE}/${id}`);
}
